/**
 * SKILL.md frontmatter `requires` — the single declared "what this skill needs
 * to run" inventory (tools / connectors / bins / native).
 *
 * Vocabulary copies:
 *   - tools + native + hostBins → `capability-requires-vocab.json`
 *     (tools/native hand-copied from describeMigrationDosiaToolNames /
 *     LEAN_GATEABLE_NATIVE_TOOLS). New bin names must be added here explicitly.
 *   - hostBins is a closed **name domain** of binaries a host may provide by
 *     itself (N-31). Being in the domain says nothing about availability:
 *     which host has which name lives only in `host-capabilities.json`
 *     (`hosts.<id>.bins.host`); the `codex` / `feishu-bot` lists are
 *     `unverified` rather than assumed.
 *   - connectors → live plugin `.mcp.json` keys (+ `chrome-devtools`).
 *   - bins domain = `dosia.json` `cliTools` ∪ `hostBins` (the two must not
 *     overlap: `check:capability-manifest` reds a cliTools name that is also a
 *     hostBins name, since cloud-locality classifies a bin by that membership).
 */
import vocabJson from "./capability-requires-vocab.json" with { type: "json" };
import optionalContract from "./skill-optional-contract.json" with { type: "json" };
export const CAPABILITY_REQUIRES_VOCAB = {
    tools: vocabJson.tools,
    native: vocabJson.native,
    hostBins: vocabJson.hostBins,
};
/** `requires.bins` domain: plugin cliTools ∪ the closed hostBins name domain. */
export function binsDomain(cliTools = []) {
    const out = [];
    const seen = new Set();
    for (const name of cliTools) {
        const trimmed = name.trim();
        if (!trimmed || seen.has(trimmed))
            continue;
        seen.add(trimmed);
        out.push(trimmed);
    }
    for (const name of CAPABILITY_REQUIRES_VOCAB.hostBins) {
        if (seen.has(name))
            continue;
        seen.add(name);
        out.push(name);
    }
    return out;
}
export function emptySkillRequires() {
    return { tools: [], connectors: [], bins: [], native: [], optional: { tools: [], connectors: [] } };
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
/**
 * Unique body-scan for DOSIA in-process tool names. Hits `\b<name>\b` or
 * `mcp__dosia-tools__<name>` (word boundary after the name). Order follows
 * `tools`. The .mjs gate reimplements the same algorithm; both are pinned to
 * `src/__fixtures__/tool-ref-detect-cases.json`.
 */
export function detectToolRefs(body, tools) {
    const hits = [];
    for (const name of tools) {
        const escaped = escapeRegExp(name);
        const bare = new RegExp(`\\b${escaped}\\b`);
        const mcp = new RegExp(`mcp__dosia-tools__${escaped}\\b`);
        if (bare.test(body) || mcp.test(body))
            hits.push(name);
    }
    return hits;
}
function readStringList(raw, axis, allowed, errors) {
    if (raw === undefined)
        return [];
    if (!Array.isArray(raw)) {
        errors.push(`requires.${axis} must be an array`);
        return [];
    }
    const out = [];
    const seen = new Set();
    for (const item of raw) {
        if (typeof item !== "string" || !item.trim()) {
            errors.push(`requires.${axis} entries must be non-empty strings`);
            continue;
        }
        const name = item.trim();
        if (allowed && !allowed.includes(name)) {
            errors.push(`unknown ${axis} name: ${name}`);
            continue;
        }
        if (seen.has(name))
            continue;
        seen.add(name);
        out.push(name);
    }
    return out;
}
/**
 * Runtime guard for a `requires` mapping. Unknown names go into `errors`
 * (not thrown) and are omitted from the returned object.
 *
 * Missing `requires` (`undefined`) is empty with no error (callers infer).
 * A present non-mapping (`null` / array / string / number) is
 * `errors: ["requires must be a mapping"]` with four empty axes.
 */
export function parseSkillRequires(raw, vocab = {}) {
    const errors = [];
    if (raw === undefined) {
        return { requires: emptySkillRequires(), errors };
    }
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        errors.push("requires must be a mapping");
        return { requires: emptySkillRequires(), errors };
    }
    const rec = raw;
    const allowedBins = vocab.bins !== undefined ? binsDomain(vocab.bins) : undefined;
    const tools = readStringList(rec.tools, "tools", vocab.tools, errors);
    const connectors = readStringList(rec.connectors, "connectors", vocab.connectors, errors);
    const optional = readOptional(rec.optional, vocab, errors, tools, connectors);
    return {
        requires: {
            tools,
            connectors,
            bins: readStringList(rec.bins, "bins", allowedBins, errors),
            native: readStringList(rec.native, "native", vocab.native, errors),
            optional,
        },
        errors,
    };
}
/**
 * `requires.optional` 允许的键，白名单闭集。
 *
 * 值来自 `skill-optional-contract.json`——门 `.mjs` 读同一份文件，两侧测试各自断言与磁盘相等。
 * 早先两处各写一份字面量、靠注释声明「逐字对齐」：注释钉不住任何东西，只有共读同一份数据才钉得住。
 */
export const OPTIONAL_AXES = optionalContract.optionalAxes;
/**
 * 投影会写出去、因此要参与可选工具泄漏检查的 frontmatter 描述字段（同一份 JSON）。
 * DOSIA frontmatter 用 camelCase，故只有 `description` 与 `descriptionEn`。
 */
export const SKILL_DESCRIPTION_FIELDS = optionalContract.descriptionFields;
/**
 * `requires.optional` 子映射。名字走与必需轴同一份白名单；与同名必需轴重复 = 错误
 * （REQ-238 R13：一个依赖只能是必需或可选之一），重复项从可选轴剔除。
 */
function readOptional(raw, vocab, errors, requiredTools, requiredConnectors) {
    if (raw === undefined)
        return { tools: [], connectors: [] };
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        errors.push("requires.optional must be a mapping");
        return { tools: [], connectors: [] };
    }
    const rec = raw;
    // 键走白名单，不是「认得的读、认不得的丢」：`optional: { native: [...] }` 或拼成 `tool:`
    // 时静默丢弃 = 作者以为声明了、门以为没声明，投影侧照单全收。
    for (const key of Object.keys(rec)) {
        if (!OPTIONAL_AXES.includes(key)) {
            errors.push(`requires.optional has unknown key: ${key}`);
        }
    }
    const tools = readStringList(rec.tools, "optional.tools", vocab.tools, errors);
    const connectors = readStringList(rec.connectors, "optional.connectors", vocab.connectors, errors);
    return {
        tools: dedupeAgainstRequired(tools, requiredTools, "tools", errors),
        connectors: dedupeAgainstRequired(connectors, requiredConnectors, "connectors", errors),
    };
}
function dedupeAgainstRequired(optional, required, axis, errors) {
    const out = [];
    for (const name of optional) {
        if (required.includes(name)) {
            errors.push(`requires.optional.${axis} duplicates requires.${axis}: ${name}`);
            continue;
        }
        out.push(name);
    }
    return out;
}
//# sourceMappingURL=skill-requires.js.map